import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';
import styles from './LookupField.module.css';

function LookupField({ 
  fieldSchema, 
  value, 
  onChange, 
  onBlur,
  onKeyDown,
  isEditing, 
  lookupData = [],
  className = '',
  autoFocus = false
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Get the fields to search and display
  const searchFields = useMemo(() => {
    return fieldSchema.fields || { name: { source: 'name' } };
  }, [fieldSchema.fields]);
  const displayField = searchFields.name?.source || 'name';
  const idField = searchFields.id?.source || 'id';

  // Filter lookup options based on search term
  const filteredOptions = useMemo(() => {
    if (!lookupData || !Array.isArray(lookupData)) return [];
    
    if (!searchTerm.trim()) return lookupData;
    
    return lookupData.filter(item => {
      return Object.values(searchFields).some(field => {
        const fieldValue = item[field.source];
        return fieldValue && String(fieldValue).toLowerCase().includes(searchTerm.toLowerCase());
      });
    });
  }, [lookupData, searchTerm, searchFields]);

  // Get display value for current selection
  const displayValue = useMemo(() => {
    if (!value || !lookupData) return '';
    
    // Handle both object and primitive values
    const valueId = typeof value === 'object' ? value.id : value;
    const item = lookupData.find(item => item[idField] === valueId);
    
    return item ? item[displayField] : (typeof value === 'object' ? value.name : value) || '';
  }, [value, lookupData, displayField, idField]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (onKeyDown) onKeyDown(e);
    
    if (!showDropdown) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setShowDropdown(true);
        setSelectedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredOptions.length) {
          selectOption(filteredOptions[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowDropdown(false);
        setSelectedIndex(-1);
        setSearchTerm('');
        inputRef.current?.blur();
        break;
      default:
        // Handle any other keys
        break;
    }
  };

  // Handle option selection
  const selectOption = (option) => {
    const newValue = {
      id: option[idField],
      name: option[displayField]
    };
    
    onChange?.(newValue);
    setShowDropdown(false);
    setSelectedIndex(-1);
    setSearchTerm('');
    
    // Focus back to input after selection
    setTimeout(() => {
      if (onBlur) onBlur();
    }, 100);
  };

  // Handle input focus
  const handleFocus = () => {
    if (isEditing) {
      setShowDropdown(true);
      setSearchTerm('');
    }
  };

  // Handle input blur
  const handleBlur = (e) => {
    // Delay blur to allow for dropdown clicks
    setTimeout(() => {
      if (!dropdownRef.current?.contains(document.activeElement)) {
        setShowDropdown(false);
        setSelectedIndex(-1);
        setSearchTerm('');
        if (onBlur) onBlur(e);
      }
    }, 150);
  };

  // Clear selection
  const clearSelection = (e) => {
    e.stopPropagation();
    onChange?.(null);
    setSearchTerm('');
    inputRef.current?.focus();
  };

  // Render read-only view
  if (!isEditing) {
    return (
      <div className={styles.readOnlyContainer}>
        {displayValue ? (
          <span className={styles.readOnlyValue}>{displayValue}</span>
        ) : (
          <span className={styles.nullValue}>N/A</span>
        )}
        
      </div>
    );
  }

  // Render editable view
  return (
    <div className={`${styles.lookupContainer} ${className}`} ref={dropdownRef}>
      <div className={styles.inputContainer}>
        <div className={styles.inputWrapper}>
          <Search className={styles.searchIcon} />
          <input
            ref={inputRef}
            type="text"
            value={showDropdown ? searchTerm : displayValue}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={styles.lookupInput}
            placeholder={`Search ${fieldSchema.lookup || 'items'}...`}
            autoFocus={autoFocus}
            autoComplete="off"
          />
          {displayValue && (
            <button
              type="button"
              onClick={clearSelection}
              className={styles.clearButton}
              title="Clear selection"
            >
              <X className={styles.clearIcon} />
            </button>
          )}
          <ChevronDown 
            className={`${styles.dropdownIcon} ${showDropdown ? styles.dropdownIconOpen : ''}`}
          />
        </div>
      </div>

      {showDropdown && (
        <div className={styles.dropdown}>
          {filteredOptions.length > 0 ? (
            <div className={styles.optionsList}>
              {filteredOptions.map((option, index) => (
                <div
                  key={`${option[idField] || 'no-id'}_${index}`}
                  className={`${styles.option} ${
                    index === selectedIndex ? styles.optionSelected : ''
                  } ${
                    value && option[idField] === (typeof value === 'object' ? value.id : value)
                      ? styles.optionCurrent
                      : ''
                  }`}
                  onClick={() => selectOption(option)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className={styles.optionContent}>
                    <div className={styles.optionName}>
                      {option[displayField]}
                    </div>
                    {searchFields.id && option[idField] && (
                      <div className={styles.optionId}>
                        ID: {option[idField]}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.noOptions}>
              {searchTerm ? `No results for "${searchTerm}"` : 'No options available'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default LookupField;